/**
 * PostModule — the post-processing stack (Graphics G4).
 *
 * Configures the engine's PostProcessingPipeline (AA + bloom + SSAO + DoF) via
 * `app.renderer.createPostProcessing(scene, camera, config)` — the renderer applies it
 * during the Render phase when enabled, so this Post-phase module's job is to BUILD the
 * pipeline (lazily, once a camera exists) and switch quality tiers. AA runs in the post
 * chain (MSAA is off), so SSAO is safe (the r182 GTAO+MSAA WGSL gotcha is sidestepped).
 * ACES (G3) stays the final tone map — the post chain runs in HDR before it.
 */
import type * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { AutoExposureSystem } from 'enginex/render/post/AutoExposure.js';
import { projectSunToScreen } from 'enginex/render/gi/GodRaysPass.js';
import { Vector3 } from 'three/webgpu';
import { createColorblindFilterNode } from 'enginex/accessibility/colorblind/ColorblindFilterNode.js';
import { sunDirection } from '../lib/lighting.js';
import { bakeLook, LUT_SIZE } from '../lib/lut_looks.js';
import { currentTod } from './SkyModule.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { bus } from '../bus.js';
import { atlas } from '../state.js';

/** `atlas:ao` — 'on' | 'off' | null (the tier decides). */
function aoOverride(): 'on' | 'off' | null {
  try { const v = localStorage.getItem('atlas:ao'); return v === 'on' || v === 'off' ? v : null; } catch { return null; }
}
import { cloudsAllowed } from '../lib/space_clouds.js';
import { type Tier, nextTier, setActiveTier, activeTier, activePreset, effectivePixelRatio } from '../lib/quality_tiers.js';

interface PostRenderer {
  createPostProcessing(scene: THREE.Scene, camera: THREE.Camera, config?: unknown): unknown;
  postProcessing: { enabled: boolean; dispose?(): void } | null;
  native: THREE.WebGPURenderer;
}

export class PostModule implements IModule {
  readonly name = 'Post';
  private app!: App;
  private built = false;
  private onKey = (e: KeyboardEvent): void => { if (e.key === 'F4') this.setTier(nextTier(activeTier())); };
  private onResize = (): void => { this.applyPixelRatio(); };

  init(app: App): void {
    this.app = app;
    this.applyPixelRatio(); // the tier's cap from the first frame (see applyPixelRatio)
    // A resolution TARGET is a ratio of the window's CSS height — re-derive it on resize.
    window.addEventListener('resize', this.onResize);
    // The active graphics settings are loaded in main.ts (loadSettings) BEFORE any module
    // builds, so activePreset() is already correct here — don't reset the tier.
    // Build lazily once the camera exists (the Post phase runs after Render each frame).
    app.world.addSystem(makeSystem('PostBuild', Phase.Post, 10, () => this.ensureBuilt()));
    // Shader-quality sync (2026-08-17 gap-closure): feeds the new pipeline
    // passes their live inputs each frame — fog/capsule sun direction from
    // LightingModule, the player capsule proxy, auto-exposure adaptation, and
    // late-wires the aerial volume once the sky's first froxel bake lands.
    app.world.addSystem(makeSystem('PostShaderSync', Phase.Post, 11, (dt) => this.syncShaderInputs(dt)));
    window.addEventListener('keydown', this.onKey);
  }

  private renderer(): PostRenderer { return this.app.renderer as unknown as PostRenderer; }
  private camera(): THREE.Camera | null { try { return this.app.renderer.cameras.main; } catch { return null; } }

  private readonly sunScratch = new Vector3();

  /** applySettings() requests are coalesced here: ONE rebuild per frame with the latest settings. */
  private rebuildPending = false;

  private ensureBuilt(): void {
    if (this.rebuildPending) {
      this.rebuildPending = false;
      this.renderer().postProcessing?.dispose?.();
      this.built = false;
    }
    if (this.built) return;
    const cam = this.camera();
    if (cam) { this.build(cam); this.built = true; }
  }

  /**
   * The render-scale cap (lib/quality_tiers effectivePixelRatio — ultra alone
   * may exceed the display's devicePixelRatio; every other tier clamps to it,
   * WHY: on a dpr-1.5 display med/high/ultra otherwise all clamp to 1.5 and the
   * ladder stops differentiating). Applied at init and BEFORE every pipeline
   * build: the pipeline measures the drawing buffer at build, and the old
   * apply-after-build left the menu, the streaming world and the pipeline's own
   * size at the uncapped ratio ("fix the render scale cap", 2026-09-08).
   * NEVER while presenting: the XR framebuffer scale is the session's
   * framebufferScaleFactor, and a mid-session pixelRatio write (Quest browser
   * dpr = 0.75!) shrank the render to 3/4 of the layer with overlapping eyes —
   * the 2026-08-16 eye-misalignment root cause. Desktop settings re-apply on
   * session end.
   */
  private applyPixelRatio(): void {
    const r = this.renderer();
    if (r.native.xr?.isPresenting) return;
    const want = effectivePixelRatio();
    if (Math.abs(r.native.getPixelRatio() - want) > 1e-6) r.native.setPixelRatio(want);
  }

  private build(cam: THREE.Camera): void {
    const r = this.renderer();
    this.applyPixelRatio(); // BEFORE createPostProcessing: the pipeline sizes itself from the drawing buffer
    // (aerial wiring is identity-latched below — see aerialWiredVol; the old
    // boolean reset here completed THE HYPERWARP REBUILD LOOP, 2026-08-23)
    // AO OVER WEBGPU (render wave 1, 2026-09-17). GTAO makes the pipeline request a NORMAL
    // MRT target on the scene pass; the July 2026 guard stripped AO here because on Dawn the
    // line geometries (built from points, no `normal` attribute) failed pipeline validation
    // once that target existed, and an invalid pipeline blacked the whole frame. The lines
    // carry a zero normal now (lib/line_geometry.ts), so AO runs on a true WebGPU backend —
    // `localStorage atlas:ao = 'off'` is the kill switch, 'on' forces it. WebGL2 keeps its
    // strip: there a material that writes fewer outputs than the pass has attachments has
    // its DRAW dropped, which is structural, not a missing attribute.
    const preset = activePreset();
    const post: Record<string, unknown> = { ...preset.post };
    // FSR 1 upscale (perf frontier 2026-09-08): the scene pass renders at
    // 1/ratio and EASU + RCAS rebuild the output size before every other pass.
    // Never while presenting in XR (the session's framebuffer scale governs).
    if (preset.upscale && preset.upscale !== 'off' && !r.native.xr?.isPresenting) post.upscale = { quality: preset.upscale, mode: preset.upscaleMode === 'taau' ? 'taau' : 'fsr' };
    // Accessibility: colourblind daltonization as the final display-referred
    // transform (graphics-menu wave — the engine pass existed, unwired).
    if (preset.colorblindMode && preset.colorblindMode !== 'off') {
      try {
        const filt = createColorblindFilterNode(preset.colorblindMode, preset.colorblindStrength ?? 1);
        post.colorblind = { transform: filt.transform };
      } catch { /* engine build without the node — skip */ }
    }
    if (this.isWebGPUBackend()) {
      if (aoOverride() === 'off') delete (post as { ao?: unknown }).ao;
    } else {
      // The MIRROR gotcha: bloom (emissive target) and AO (normal target) make the
      // pipeline request MRT on the scene pass; on the WebGL2 FALLBACK every scene
      // material that doesn't write those extra outputs hits "Active draw buffers
      // with missing fragment shader outputs" and the draw is DROPPED — a black
      // world (only the few MRT-writing materials survive). Default tier is 'high'
      // (bloom+ao on), so every WebGL2 fallback browser rendered nothing until
      // this guard.
      delete (post as { bloom?: unknown }).bloom;
      delete (post as { ao?: unknown }).ao;
    }

    // Phase C: composite the volumetric clouds as a DEPTH-AWARE post pass (not in
    // SkyModule.backgroundNode — see SkyModule.ts). The engine PostProcessingPipeline
    // `clouds` hook composites `sceneColor·(1−a)+rgb` over SKY pixels (depth ≈ far)
    // only, so opaque geometry occludes the clouds, and the cloud node ray-marches
    // from the REAL camera position ⇒ world-anchored + parallaxing (the band-pinned-
    // to-the-view bug is fixed). WeatherModule keeps driving the cloud uniforms
    // (coverage / density / tint / night-lighting) wherever the node composites.
    // Guard: CloudModule may be absent in headless/test environments.
    const cloudMod = this.app.getModule('Clouds') as
      | {
          renderer?: { buildCompositeNode(): { rgb: unknown; a: unknown } };
          buildCompositeNode?(): { rgb: unknown; a: unknown };
          lowRenderer?: unknown;
          setQualityPreset?(p: 'low' | 'medium' | 'high' | 'ultra'): void;
          setLowQualityPreset?(p: 'low' | 'medium' | 'high' | 'ultra'): void;
          setParallaxScale?(v: number): void;
          meshFieldActive?: boolean;
          setMeshFieldEnabled?(on: boolean): void;
        }
      | undefined;
    // 2026-08-19: the MESH-cloud field (real 3D volumes in the world) is the
    // primary cloud renderer when configured — the tier's cloudsEnabled flag
    // gates it exactly like the post-pass composite it replaces. When the
    // field is active, the screen-space composite below is SKIPPED (no double
    // clouding); the slab path stays as the fallback for apps without a
    // configured mesh field.
    // NO CLOUDS IN SPACE (2026-09-10): a graphics-menu toggle rebuilds this
    // pipeline mid-flight, and the tier's flag alone re-armed the mesh field
    // (and the composite) over the sector — the sector has no atmosphere.
    // The same rule SpaceModule applies on activate / deactivate.
    const cloudsWanted = cloudsAllowed(!!preset.cloudsEnabled, atlas.spaceFlight);
    cloudMod?.setMeshFieldEnabled?.(cloudsWanted);
    const meshCloudsOn = !!cloudMod?.meshFieldActive;
    if (cloudsWanted && !meshCloudsOn && cloudMod?.renderer) {
      // Cloud march quality follows the graphics tier (the raymarch is the
      // most expensive pass in the chain — ~half the post budget at 'medium').
      // One preset below the tier name: the jittered march start makes each
      // step count look a preset higher, so this trades invisible quality for
      // a large frame-time win. Rebuilding the pipeline (we are inside build())
      // picks up the new step constants.
      // Keys are TIER ids ('med', not 'medium' — the old 'medium' key was dead
      // and med only got its preset via the fallback; an edit to the fallback
      // would have silently changed med-tier cloud quality).
      const marchOverride = preset.cloudMarchQuality ?? 'auto';
      const cloudPreset = marchOverride !== 'auto' ? marchOverride
        : ({ med: 'low', high: 'medium', ultra: 'high' } as const)[
          activeTier() as 'med' | 'high' | 'ultra'
        ] ?? 'low';
      cloudMod.setQualityPreset?.(cloudPreset);
      // Camera-translation response (live uniform — taste knob). Tier default
      // 1.0: world-anchored — clouds are stationary in the world; walk-speed
      // parallax is physically small on the 3.8 km deck, the low puffs make it
      // legible. 0 would freeze the sky to the camera (HDRP camera space).
      cloudMod.setParallaxScale?.(preset.cloudParallax ?? 1);
      // Low scattered layer (walk-speed parallax legibility): the module-level
      // node composites low OVER high. Zero cost when the tier keeps it off —
      // the low march is simply not in the graph.
      const wantLow = preset.lowClouds && !!cloudMod.lowRenderer && !!cloudMod.buildCompositeNode;
      // Near puffs show step/noise grain far more than the distant deck
      // ("pixelated" field report) — run the low layer one preset FINER than
      // the deck: high tier → 'medium', ultra → 'high'. Thin slab ⇒ short
      // in-cloud paths, so the finer stepping stays affordable.
      if (wantLow) cloudMod.setLowQualityPreset?.(activeTier() === 'ultra' ? 'high' : 'medium');
      post.clouds = {
        node: wantLow
          ? cloudMod.buildCompositeNode!()
          : cloudMod.renderer.buildCompositeNode(),
        // Under TRAA the temporal resolve smears the clouds' blue-noise
        // dither and flickers the depth mask at building silhouettes (the
        // blocky-cloud report, 2026-08-17) — composite them AFTER the AA
        // stage instead. Costs bloom on clouds only.
        afterAA: !!(post as { traa?: { enabled?: boolean } }).traa?.enabled || preset.upscaleMode === 'taau',
        // Perf frontier item 6: raymarch at a fraction of the drawing buffer (RTT), composite bilinearly.
        resolutionScale: preset.cloudResolution ?? 1,
      };
    }

    // Aerial-perspective composite: wire the sky's baked froxel volume when
    // the tier wants it AND the volume exists (LUT pipeline + first tier-B
    // bake done). The texture object is identity-stable across re-bakes, so
    // wiring once holds; if the bake hasn't landed yet, syncShaderInputs
    // triggers ONE rebuild when it appears.
    if (preset.aerialEnabled) {
      const vol = this.aerialVolume();
      if (vol) {
        post.aerial = { volume: vol, maxDistanceKm: 32, kmPerWorldUnit: 0.001 };
        this.aerialWiredVol = vol;
      } else {
        this.aerialWiredVol = null; // this pipeline has no aerial — a future volume may re-attempt (bounded)
      }
    } else {
      this.aerialWiredVol = null;
    }

    // The colour LOOK: a procedural 16³ LUT for the engine's lut3D pass (shader wave 2026-09-08).
    if (preset.look && preset.look !== 'none') post.lut3D = { lut: bakeLook(preset.look), size: LUT_SIZE, intensity: 1 };
    else delete post.lut3D;
    r.createPostProcessing(this.app.world.scene, cam, post);
    if (r.postProcessing) r.postProcessing.enabled = true;
  }

  /** The froxel-volume IDENTITY the built pipeline carries (null = none). */
  private aerialWiredVol: THREE.Data3DTexture | null = null;
  /** Late-wiring attempt limiter: at most 3 rebuilds per volume identity,
   *  5 s apart. WHY (the hyperwarp destroyed-buffer flood, forensically
   *  pinned 2026-08-23): the sky re-bakes its froxel volume on its own
   *  cadence; when a rebuild happened to run while the volume was
   *  momentarily null, the old boolean latch stayed false and the next
   *  sync re-triggered — an ENDLESS full post-chain rebuild at the bake
   *  cadence, each cycle destroying every pass's uniform buffers (bloom
   *  et al) while the in-flight frame still referenced them. */
  private aerialAttempt: { vol: THREE.Data3DTexture | null; n: number; at: number } = { vol: null, n: 0, at: 0 };
  private autoExposureSys: AutoExposureSystem | null = null;
  private savedExposure: number | null = null;

  /** The sky's baked aerial froxel texture, when available. */
  private aerialVolume(): THREE.Data3DTexture | null {
    const sky = this.app.getModule('AtlasSky') as
      | { _engineSky?: { aerialTexture: THREE.Data3DTexture | null } }
      | undefined;
    return sky?._engineSky?.aerialTexture ?? null;
  }

  /** The engine pipeline instance (widened past the local facade type). */
  private pipelineFull(): {
    volumetricFogUniforms?: {
      sunDirection: { value: THREE.Vector3 };
      color: { value: THREE.Vector3 };
      sunColor: { value: THREE.Vector3 };
    } | null;
    capsuleShadowsUniforms?: {
      lightDirection: { value: THREE.Vector3 };
      setCapsule(i: number, c: { a: [number, number, number]; b: [number, number, number]; radius: number } | null): void;
    } | null;
    setRenderScale?: (scale: number) => boolean;
    renderScale?: number;
    scenePassRenderTarget?: { width: number; height: number } | null;
  } | null {
    return (this.renderer().postProcessing as ReturnType<PostModule['pipelineFull']>) ?? null;
  }

  /** Per-frame inputs for the new shader passes (Post phase, after build). */
  private syncShaderInputs(dt: number): void {
    if (!this.built) return;
    const preset = activePreset();
    const pp = this.pipelineFull();
    if (!pp) return;

    // God rays (graphics-menu wave): drive the pass's live sun uniforms from
    // the same sun the sky uses — intensity 0 below the horizon.
    {
      const gr = (pp as unknown as { godRays?: { uniforms?: { sunScreenPos: { value: { set(x: number, y: number): void } }; intensity: { value: number } } } }).godRays;
      const cam = this.camera();
      if (gr?.uniforms && cam) {
        const sd = sunDirection(currentTod());
        const proj = projectSunToScreen(this.sunScratch.set(sd.x, sd.y, sd.z), cam);
        gr.uniforms.sunScreenPos.value.set(proj.x, proj.y);
        gr.uniforms.intensity.value = proj.sunBelowHorizon ? 0 : 1;
      }
    }

    // Late aerial wiring: the first froxel bake may land after the pipeline
    // built — rebuild when a NEW volume identity appears, at most 3 attempts
    // per identity, 5 s apart (the loop-proof latch; see aerialAttempt).
    if (preset.aerialEnabled) {
      const vol = this.aerialVolume();
      if (vol && vol !== this.aerialWiredVol) {
        const a = this.aerialAttempt;
        const nowMs = performance.now();
        if (a.vol !== vol) { a.vol = vol; a.n = 0; }
        if (a.n < 3 && nowMs - a.at > 5000) {
          a.n++;
          a.at = nowMs;
          // Rebuild only: applySettings() also emits atlas:quality-changed,
          // which makes StreamingModule evict EVERY warm asset.
          this.rebuildPending = true;
          return;
        }
      }
    }

    // Sun direction for fog + capsule shadows (LightingModule's sun position
    // IS the direction — it targets the origin).
    const lighting = this.app.getModule('Lighting') as
      | { sunLight?: { position: THREE.Vector3 } }
      | undefined;
    const sunPos = lighting?.sunLight?.position;
    if (sunPos && sunPos.lengthSq() > 1e-6) {
      pp.volumetricFogUniforms?.sunDirection.value.copy(sunPos).normalize();
      pp.capsuleShadowsUniforms?.lightDirection.value.copy(sunPos).normalize();
    }

    // Volumetric fog COLOUR tracks the atmosphere (2026-09-14). Nothing drove
    // it, so the pass sat at the engine default blue-grey [0.65,0.72,0.82] at
    // every hour — a daylight haze colour hanging over midnight and sunset,
    // fighting the analytic FogExp2 that AtmosphereModule does drive. Same
    // source as that fog, so the two layers agree by construction.
    const vf = pp.volumetricFogUniforms;
    if (vf) {
      const fog = this.app.world.scene.fog as THREE.FogExp2 | THREE.Fog | null;
      if (fog?.color) vf.color.value.set(fog.color.r, fog.color.g, fog.color.b);
      // In-scatter takes the sun's own colour (warm at low elevation), not the
      // fog body colour — otherwise sunset shafts read grey.
      const sunCol = (lighting as { sunLight?: { color?: THREE.Color } } | undefined)?.sunLight?.color;
      if (sunCol) vf.sunColor.value.set(sunCol.r, sunCol.g, sunCol.b);
    }

    // Player capsule proxy (slot 0) — grounds the avatar on tiers without CSM.
    if (pp.capsuleShadowsUniforms) {
      // Render space: the pass reconstructs points from the REBASED camera.
      const sp = this.app.world.scene.position;
      const x = atlas.localX + sp.x; const y = atlas.localY + sp.y; const z = atlas.localZ + sp.z;
      if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
        pp.capsuleShadowsUniforms.setCapsule(0, {
          a: [x, y + 0.35, z], b: [x, y + 1.45, z], radius: 0.38,
        });
      }
    }

    // Auto-exposure: engine system executed under our gate so toggling never
    // needs system add/remove; disabling restores the pre-adaptation exposure.
    if (preset.autoExposure) {
      if (!this.autoExposureSys) {
        const native = this.renderer().native;
        this.savedExposure = native.toneMappingExposure;
        this.autoExposureSys = new AutoExposureSystem({
          renderer: native,
          getRenderTarget: () => this.pipelineFull()?.scenePassRenderTarget ?? null,
        });
      }
      // Live adaptation-strength knob (menu slider) — 0 pins base exposure.
      this.autoExposureSys.controller.setStrength(preset.autoExposureStrength ?? 1);
      // Metering is a live switch too (2026-09-15): the next reading uses it.
      this.autoExposureSys.controller.setMetering(preset.autoExposureMetering ?? 'average');
      this.autoExposureSys.execute(dt, this.app.world as never);
    } else if (this.autoExposureSys) {
      if (this.savedExposure !== null) this.renderer().native.toneMappingExposure = this.savedExposure;
      this.autoExposureSys = null;
      this.savedExposure = null;
    }
  }

  /** True on the real WebGPU (Dawn) backend; false when the WebGPURenderer falls back to WebGL2. */
  private isWebGPUBackend(): boolean {
    const backend = (this.renderer().native as unknown as { backend?: { constructor?: { name?: string } } }).backend;
    return backend?.constructor?.name === 'WebGPUBackend';
  }

  /** Switch to a clean quality preset (clears per-feature customisation), then apply. */
  setTier(tier: Tier): void {
    setActiveTier(tier);
    this.applySettings();
  }

  /**
   * Rebuild the post pipeline from the LIVE graphics settings + re-scale the rest of the
   * pipeline. Call after a preset change OR any per-feature change (the Esc graphics menu
   * does, via activePreset()'s setters). TSL is statically compiled per config, so a change
   * requires a pipeline rebuild.
   */
  applySettings(): void {
    // Coalesced (perf sweep 2026-08-22): the boot sequence called this from the
    // tier load, the VR optimizer, the main menu and the aerial-volume wiring —
    // seven synchronous pipeline builds (each compiles its passes). Now the
    // PostBuild system rebuilds ONCE on the next Post phase with the final settings.
    this.rebuildPending = true;
    bus.emit('atlas:quality-changed', {}); // G5: Lighting/Culling/Streaming rescale from activePreset()
    bus.emit('atlas:environment-updated', {}); // re-apply fog/weather with the new scales
  }

  /**
   * Move the FSR render scale LIVE (the adaptive governor's lever) — no
   * rebuild. False when the built pipeline has no upscale stage.
   */
  setRenderScaleLive(scale: number): boolean {
    const pp = this.pipelineFull();
    return pp?.setRenderScale ? pp.setRenderScale(scale) : false;
  }
  /** The upscale ratio the built pipeline is running (1 = native). */
  get liveRenderScale(): number { return this.pipelineFull()?.renderScale ?? 1; }

  /** Test/inspection hooks. */
  get currentTier(): Tier { return activeTier(); }
  get pipeline(): { enabled: boolean } | null { return this.renderer().postProcessing; }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.onResize);
    if (this.autoExposureSys && this.savedExposure !== null) {
      this.renderer().native.toneMappingExposure = this.savedExposure;
    }
    this.autoExposureSys = null;
    this.renderer().postProcessing?.dispose?.();
  }
}
